// Link previews: which mirror makes the card for a person's link (#578). The person who posts the link picks, per
// site, in Settings → Link previews. The choice is kept with their account (`linkPreviews` in /api/me/settings) and
// the room reads it when it makes the card, so everyone sees the card that the poster's choice made.
//
// A choice is one of: "auto" (the default: the server's ordered list), "unmodified" (no mirror: the site's own tags,
// so no video for Instagram and TikTok), or a mirror's host from the server's list (GET /api/link-previews). The
// server tries a picked mirror first, then the automatic list, and ignores any host that is not on its list. The apps
// never hard-code a host: they draw the list that the server sends.

/** The sites that have a choice. */
export const PREVIEW_SITES = ["instagram", "x", "tiktok", "reddit"] as const;
export type PreviewSite = (typeof PREVIEW_SITES)[number];

/** The default: the server's ordered list of mirrors. */
export const PREVIEW_AUTO = "auto";
/** No mirror: the site's own tags. No fallback to the mirrors. */
export const PREVIEW_UNMODIFIED = "unmodified";

/** What a person picked, per site. A missing site is Automatic. A value is PREVIEW_UNMODIFIED or a host on the server's list. */
export type LinkPreviewChoices = Partial<Record<PreviewSite, string>>;

/** One site's row in GET /api/link-previews. */
export interface LinkPreviewSite {
  site: PreviewSite;
  /** What the picker calls the site: "Instagram". */
  name: string;
  /** The hosts a person may pick, in the order the server tries them on Automatic, then the alternates. */
  mirrors: string[];
  /** Whether Unmodified can still give a video: the site's own tags keep a video on X and Reddit, and carry none on Instagram and TikTok. */
  unmodifiedVideo: boolean;
}

/** GET /api/link-previews: the sites and the hosts that the server accepts. */
export interface LinkPreviewList {
  sites: LinkPreviewSite[];
}

/** What a picker shows for a site: the stored value, or Automatic when none is stored. */
export const linkPreviewChoice = (choices: LinkPreviewChoices | undefined, site: PreviewSite): string => choices?.[site] ?? PREVIEW_AUTO;

/**
 * The words for a choice in the picker: "Automatic", "Unmodified (no video)" ("Unmodified" for a site whose own tags can
 * keep a video, `unmodifiedVideo`), or the host.
 */
export function linkPreviewLabel(choice: string, unmodifiedVideo = false): string {
  if (choice === PREVIEW_AUTO) return "Automatic";
  if (choice === PREVIEW_UNMODIFIED) return unmodifiedVideo ? "Unmodified" : "Unmodified (no video)";
  return choice;
}

/**
 * The choices a site's picker shows, in order: Automatic, Unmodified, then each host the server listed. A picked host that
 * the list does not hold yet (the list has not loaded) keeps its row, so the picker never holds a value with no row.
 * Cases: shared/cases/link-previews.json.
 */
export function linkPreviewChoices(mirrors: readonly string[], picked: string): string[] {
  const out = [PREVIEW_AUTO, PREVIEW_UNMODIFIED, ...mirrors];
  return out.includes(picked) ? out : [...out, picked];
}
